import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { FiHome, FiBriefcase, FiUsers, FiDollarSign, FiTool, FiKey, FiShoppingCart, FiTrendingUp, FiClipboard, FiUserCheck, FiSettings, FiCalendar, FiMapPin } from "react-icons/fi";
import { API_BASE_URL } from "../services/api";
import "./PublicIntakePage.css";

interface PracticeArea {
  code: string;
  name: string;
  description: string;
  requiresCountry?: boolean;
}

interface TemplateOption {
  id: string;
  template_name: string;
  template_description: string;
  country: string | null;
  visa_type: string | null;
  case_type: string | null;
  practice_area: string;
}

interface SelectionState {
  practiceArea?: PracticeArea | null;
  country?: string | null;
  visaType?: string | null;
  caseType?: string | null;
  template?: TemplateOption | null;
}

enum SelectionStep {
  ValidateLink = "ValidateLink",
  PracticeArea = "PracticeArea",
  Country = "Country",
  VisaType = "VisaType",
  CaseType = "CaseType",
  ConfirmTemplate = "ConfirmTemplate",
  Loading = "Loading",
  Error = "Error",
}

const PublicIntakePage: React.FC = () => {
  const { linkId } = useParams<{ linkId: string }>();

  const [step, setStep] = useState<SelectionStep>(SelectionStep.ValidateLink);
  const [error, setError] = useState<string | null>(null);
  const [practiceAreas, setPracticeAreas] = useState<PracticeArea[]>([]);
  const [templates, setTemplates] = useState<TemplateOption[]>([]);
  const [selection, setSelection] = useState<SelectionState>({});
  const [loading, setLoading] = useState<boolean>(false);

  const currentPracticeArea = selection.practiceArea;

  // Detect if this is a real estate professional based on practice areas
  const isRealEstate = useMemo(() => {
    return practiceAreas.length > 0 && practiceAreas.every(
      (pa) =>
        pa.code === "real_estate_transactions_home_purchase" ||
        pa.code === "real_estate_transactions_home_sale" ||
        pa.code === "property_management" ||
        pa.code === "tenant_services" ||
        pa.code === "property_maintenance" ||
        pa.code === "open_house_buyer" ||
        pa.code === "open_house_rental"
    );
  }, [practiceAreas]);

  useEffect(() => {
    if (!linkId) {
      setError("Invalid link. Please verify the URL provided.");
      setStep(SelectionStep.Error);
      return;
    }

    const validate = async () => {
      setLoading(true);
      try {
        const infoRes = await fetch(`${API_BASE_URL}/firm-link/${linkId}`);
        if (!infoRes.ok) {
          throw new Error(
            infoRes.status === 404
              ? "Link not found."
              : "Failed to validate link."
          );
        }

        const areasRes = await fetch(
          `${API_BASE_URL}/firm-link/${linkId}/practice-areas`
        );
        if (!areasRes.ok) {
          throw new Error("Failed to load available practice areas.");
        }
        const areasData = await areasRes.json();

        const rawPracticeAreas: PracticeArea[] = areasData.practiceAreas || [];

        const expandedPracticeAreas: PracticeArea[] = [];

        rawPracticeAreas.forEach((area) => {
          if (area.code === "real_estate_transactions") {
            expandedPracticeAreas.push(
              {
                ...area,
                code: "real_estate_transactions_home_purchase",
                name: "Home Purchase",
                description:
                  "Comprehensive intake form for clients looking to purchase a home.",
              },
              {
                ...area,
                code: "real_estate_transactions_home_sale",
                name: "Home Sale",
                description:
                  "Comprehensive intake form for clients looking to sell their home.",
              }
            );
          } else {
            expandedPracticeAreas.push(area);
          }
        });

        setPracticeAreas(expandedPracticeAreas);
        setStep(SelectionStep.PracticeArea);
      } catch (err) {
        console.error("❌ Link validation error:", err);
        setError(
          err instanceof Error ? err.message : "Failed to load intake link."
        );
        setStep(SelectionStep.Error);
      } finally {
        setLoading(false);
      }
    };

    validate();
  }, [linkId]);

  const handlePracticeAreaSelect = async (area: PracticeArea) => {
    setTemplates([]);

    if (!linkId) {
      setError("Missing link identifier.");
      setStep(SelectionStep.Error);
      return;
    }

    if (
      area.code === "real_estate_transactions_home_purchase" ||
      area.code === "real_estate_transactions_home_sale"
    ) {
      const basePracticeAreaCode = "real_estate_transactions";
      const caseType =
        area.code === "real_estate_transactions_home_purchase"
          ? "buying"
          : "selling";

      setSelection({
        practiceArea: {
          ...area,
          code: basePracticeAreaCode,
        },
        caseType,
      });

      await fetchTemplates({ practiceArea: basePracticeAreaCode, caseType });
      return;
    }

    setSelection({ practiceArea: area });

    if (area.requiresCountry) {
      setStep(SelectionStep.Country);
    } else {
      await fetchTemplates({ practiceArea: area.code });
    }
  };

  const fetchTemplates = async (filters: {
    practiceArea: string;
    country?: string | null;
    visaType?: string | null;
    caseType?: string | null;
  }) => {
    if (!linkId) {
      return;
    }

    setLoading(true);
    setTemplates([]);

    try {
      const params = new URLSearchParams();
      params.append("practiceArea", filters.practiceArea);
      if (filters.country) params.append("country", filters.country);
      if (filters.visaType) params.append("visaType", filters.visaType);
      if (filters.caseType) params.append("caseType", filters.caseType);

      const response = await fetch(
        `${API_BASE_URL}/firm-link/${linkId}/templates?${params.toString()}`
      );
      if (!response.ok) {
        throw new Error("Failed to load form templates for selection.");
      }
      const data = await response.json();
      const templateItems: TemplateOption[] = data.templates || [];
      setTemplates(templateItems);

      if (filters.practiceArea === "immigration") {
        if (filters.visaType) {
          if (templateItems.length === 1) {
            // For immigration with visa type selected, skip confirmation and go directly to form
            const template = templateItems[0];
            const practiceAreaCode = filters.practiceArea || selection.practiceArea?.code || "";
            const country = filters.country || selection.country || "";
            const visaType = filters.visaType || selection.visaType || "";
            const formUrl = `/consultation/${template.id}?link=${linkId}&practiceArea=${practiceAreaCode}&country=${country}&visaType=${visaType}`;
            
            // Redirect directly to the form
            window.location.href = formUrl;
            return;
          } else {
            setStep(SelectionStep.VisaType);
          }
        } else {
          setSelection((prev) => ({ ...prev, template: null }));
          setStep(SelectionStep.VisaType);
        }
      } else {
        if (templateItems.length === 1) {
          // For non-immigration (e.g., real estate, personal injury), skip confirmation and go directly to form
          const template = templateItems[0];
          const practiceAreaCode = filters.practiceArea || selection.practiceArea?.code || "";
          const country = filters.country || selection.country || "";
          const caseType = filters.caseType || selection.caseType || "";
          const formUrl = `/consultation/${template.id}?link=${linkId}&practiceArea=${practiceAreaCode}&country=${country}&caseType=${caseType}`;
          
          // Redirect directly to the form
          window.location.href = formUrl;
          return;
        } else {
          setSelection((prev) => ({ ...prev, template: null }));
          setStep(SelectionStep.CaseType);
        }
      }
    } catch (err) {
      console.error("❌ Template fetch error:", err);
      setError(
        err instanceof Error ? err.message : "Failed to retrieve templates."
      );
      setStep(SelectionStep.Error);
    } finally {
      setLoading(false);
    }
  };

  const handleCountrySelect = async (country: string) => {
    const practiceArea = selection.practiceArea;
    if (!practiceArea) return;

    const newSelection = {
      ...selection,
      country,
      template: null,
      visaType: null,
      caseType: null,
    };
    setSelection(newSelection);

    await fetchTemplates({ practiceArea: practiceArea.code, country });
  };

  const handleVisaTypeSelect = async (visaType: string) => {
    const practiceArea = selection.practiceArea;
    if (!practiceArea) return;

    const newSelection = { ...selection, visaType, template: null };
    setSelection(newSelection);

    await fetchTemplates({
      practiceArea: practiceArea.code,
      country: newSelection.country,
      visaType,
    });
  };

  const handleCaseTypeSelect = async (caseType: string) => {
    const practiceArea = selection.practiceArea;
    if (!practiceArea) return;

    const newSelection = { ...selection, caseType };
    setSelection(newSelection);

    await fetchTemplates({
      practiceArea: practiceArea.code,
      caseType,
    });
  };

  const handleTemplateSelect = (template: TemplateOption) => {
    // Skip confirmation and go directly to the form
    const practiceAreaCode = selection.practiceArea?.code || "";
    const country = selection.country || "";
    const visaType = selection.visaType || "";
    const caseType = selection.caseType || "";
    const formUrl = `/consultation/${template.id}?link=${linkId}&practiceArea=${practiceAreaCode}&country=${country}&visaType=${visaType}&caseType=${caseType}`;
    
    // Redirect directly to the form
    window.location.href = formUrl;
  };

  const goBackToPracticeArea = () => {
    setStep(SelectionStep.PracticeArea);
  };

  const goBackToCountry = () => {
    setStep(SelectionStep.Country);
  };

  const goBackToVisaType = () => {
    setStep(SelectionStep.VisaType);
  };

  const resetToPracticeArea = () => {
    setSelection({});
    setTemplates([]);
    setStep(SelectionStep.PracticeArea);
  };

  const displayTemplates = useMemo(() => templates, [templates]);

  const renderContent = () => {
    if (loading && step !== SelectionStep.PracticeArea) {
      return (
        <div className="pi-loading">
          <div className="pi-spinner" />
          <p>Loading options…</p>
        </div>
      );
    }

    switch (step) {
      case SelectionStep.PracticeArea:
        return (
          <div className="pi-card">
            <h2>{isRealEstate ? "Select Service Type" : "Select Practice Area"}</h2>
            <p className="pi-subtitle">
              {isRealEstate
                ? "Choose the service that best matches your property needs."
                : "Choose the area that best matches your legal matter."}
            </p>
            <div className="pi-options">
              {practiceAreas.map((area) => (
                <button
                  key={area.code}
                  className={`pi-option ${
                    selection.practiceArea?.code === area.code ? "selected" : ""
                  }`}
                  onClick={() => handlePracticeAreaSelect(area)}
                >
                  <div className="pi-option-content">
                    <div className="pi-option-text">
                      <div className="pi-option-title">
                        {getPracticeAreaIcon(area)}
                        <span>{area.name}</span>
                      </div>
                      <div className="pi-option-description">
                        {area.description}
                      </div>
                    </div>
                    <div className="pi-option-icon-container">
                      {getPracticeAreaDecorativeIcon(area)}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        );

      case SelectionStep.Country:
        return (
          <div className="pi-card">
            <BackButton onBack={goBackToPracticeArea} />
            <h2>Select Country</h2>
            <div className="pi-options">
              {["Canada", "United States"].map((country) => (
                <button
                  key={country}
                  className={`pi-option ${
                    selection.country === country ? "selected" : ""
                  }`}
                  onClick={() => handleCountrySelect(country)}
                >
                  <div className="pi-option-title">{country}</div>
                </button>
              ))}
            </div>
          </div>
        );

      case SelectionStep.VisaType:
        return (
          <div className="pi-card">
            <BackButton onBack={goBackToCountry} />
            <h2>Select Immigration Pathway</h2>
            <p className="pi-subtitle">
              Pick the visa type that fits your situation.
            </p>
            <div className="pi-options">
              {displayTemplates.length > 0
                ? displayTemplates.map((template) => (
                    <button
                      key={template.id}
                      className={`pi-option ${
                        selection.template?.id === template.id ? "selected" : ""
                      }`}
                      onClick={() => handleTemplateSelect(template)}
                    >
                      <div className="pi-option-title">
                        {template.template_name}
                      </div>
                      <div className="pi-option-description">
                        {template.template_description}
                      </div>
                    </button>
                  ))
                : [
                    "express_entry",
                    "business_immigration",
                    "family_sponsorship",
                    "study_permit",
                    "EB1",
                    "O1",
                    "H1B",
                    "J1",
                    "L1",
                  ].map((code) => (
                    <button
                      key={code}
                      className={`pi-option ${
                        selection.visaType === code ? "selected" : ""
                      }`}
                      onClick={() => handleVisaTypeSelect(code)}
                    >
                      <div className="pi-option-title">{formatLabel(code)}</div>
                    </button>
                  ))}
            </div>
          </div>
        );

      case SelectionStep.CaseType:
        return (
          <div className="pi-card">
            <BackButton onBack={goBackToPracticeArea} />
            <h2>Select Case Type</h2>
            <p className="pi-subtitle">
              Pick the option that best matches your matter.
            </p>
            <div className="pi-options">
              {displayTemplates.length > 0
                ? displayTemplates.map((template) => (
                    <button
                      key={template.id}
                      className={`pi-option ${
                        selection.template?.id === template.id ? "selected" : ""
                      }`}
                      onClick={() => handleTemplateSelect(template)}
                    >
                      <div className="pi-option-title">
                        {template.template_name}
                      </div>
                      <div className="pi-option-description">
                        {template.template_description}
                      </div>
                    </button>
                  ))
                : [
                    "car_accident",
                    "slip_and_fall",
                    "medical_malpractice",
                    "work_injury",
                    "occupational_disease",
                  ].map((code) => (
                    <button
                      key={code}
                      className={`pi-option ${
                        selection.caseType === code ? "selected" : ""
                      }`}
                      onClick={() => handleCaseTypeSelect(code)}
                    >
                      <div className="pi-option-title">{formatLabel(code)}</div>
                    </button>
                  ))}
            </div>
          </div>
        );

      case SelectionStep.ConfirmTemplate:
        return selection.template ? (
          <div className="pi-card">
            <BackButton
              onBack={
                currentPracticeArea?.code === "immigration"
                  ? goBackToVisaType
                  : goBackToPracticeArea
              }
            />
            <h2>You're all set</h2>
            <p className="pi-subtitle">
              Proceed to the consultation form to finish your submission.
            </p>
            <div className="pi-summary">
              <p>
                <strong>Practice Area:</strong> {selection.practiceArea?.name}
              </p>
              {selection.country && (
                <p>
                  <strong>Country:</strong> {selection.country}
                </p>
              )}
              {selection.visaType && (
                <p>
                  <strong>Visa Type:</strong> {formatLabel(selection.visaType)}
                </p>
              )}
              {selection.caseType && (
                <p>
                  <strong>Case Type:</strong> {formatLabel(selection.caseType)}
                </p>
              )}
              <p>
                <strong>Form Template:</strong>{" "}
                {selection.template.template_name}
              </p>
              <p className="pi-template-description">
                {selection.template.template_description}
              </p>
            </div>
            <div className="pi-actions">
              <button className="pi-secondary" onClick={resetToPracticeArea}>
                Start Over
              </button>
              <a
                className="pi-primary"
                href={`/consultation/${
                  selection.template.id
                }?link=${linkId}&practiceArea=${
                  selection.practiceArea?.code || ""
                }&country=${selection.country || ""}&visaType=${
                  selection.visaType || ""
                }&caseType=${selection.caseType || ""}`}
              >
                Continue to Form
              </a>
            </div>
          </div>
        ) : null;

      case SelectionStep.Error:
        return (
          <div className="pi-error">
            <h2>Link Not Available</h2>
            <p>{error}</p>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="public-intake-page">
      <div className="pi-header">
        <h1>{isRealEstate ? "Start Your Property Service Request" : "Begin Your Consultation"}</h1>
        <p>
          {isRealEstate
            ? "Answer a few quick questions to access the right form for your property needs."
            : "Answer a few quick questions to access the right intake form."}
        </p>
      </div>
      {renderContent()}
    </div>
  );
};

interface BackButtonProps {
  onBack: () => void;
}

const BackButton: React.FC<BackButtonProps> = ({ onBack }) => (
  <button className="pi-back" onClick={onBack}>
    ← Back
  </button>
);

function formatLabel(value: string): string {
  return value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function getPracticeAreaIcon(area: PracticeArea) {
  if (area.code === "real_estate_transactions_home_purchase") {
    return React.createElement(FiHome as any, {
      className: "pi-option-title-icon",
    });
  }

  if (area.code === "real_estate_transactions_home_sale") {
    return React.createElement(FiDollarSign as any, {
      className: "pi-option-title-icon",
    });
  }

  if (area.code === "property_management") {
    return React.createElement(FiBriefcase as any, {
      className: "pi-option-title-icon",
    });
  }

  if (area.code === "tenant_services") {
    return React.createElement(FiUsers as any, {
      className: "pi-option-title-icon",
    });
  }

  if (area.code === "property_maintenance") {
    return React.createElement(FiTool as any, {
      className: "pi-option-title-icon",
    });
  }

  if (area.code === "open_house_buyer") {
    return React.createElement(FiCalendar as any, {
      className: "pi-option-title-icon",
    });
  }

  if (area.code === "open_house_rental") {
    return React.createElement(FiMapPin as any, {
      className: "pi-option-title-icon",
    });
  }

  return React.createElement(FiHome as any, {
    className: "pi-option-title-icon",
  });
}

// Decorative icon on the right side for visual appeal
function getPracticeAreaDecorativeIcon(area: PracticeArea) {
  if (area.code === "real_estate_transactions_home_purchase") {
    return React.createElement(FiKey as any, {
      className: "pi-option-decorative-icon",
      style: { strokeWidth: 1.5 }
    });
  }

  if (area.code === "real_estate_transactions_home_sale") {
    return React.createElement(FiTrendingUp as any, {
      className: "pi-option-decorative-icon",
      style: { strokeWidth: 1.5 }
    });
  }

  if (area.code === "property_management") {
    return React.createElement(FiClipboard as any, {
      className: "pi-option-decorative-icon",
      style: { strokeWidth: 1.5 }
    });
  }

  if (area.code === "tenant_services") {
    return React.createElement(FiUserCheck as any, {
      className: "pi-option-decorative-icon",
      style: { strokeWidth: 1.5 }
    });
  }

  if (area.code === "property_maintenance") {
    return React.createElement(FiSettings as any, {
      className: "pi-option-decorative-icon",
      style: { strokeWidth: 1.5 }
    });
  }

  if (area.code === "open_house_buyer") {
    return React.createElement(FiKey as any, {
      className: "pi-option-decorative-icon",
      style: { strokeWidth: 1.5 }
    });
  }

  if (area.code === "open_house_rental") {
    return React.createElement(FiHome as any, {
      className: "pi-option-decorative-icon",
      style: { strokeWidth: 1.5 }
    });
  }

  return React.createElement(FiHome as any, {
    className: "pi-option-decorative-icon",
    style: { strokeWidth: 1.5 }
  });
}

export default PublicIntakePage;
